import { when } from 'mobx'
import { ViewState } from 'react-map-gl/mapbox'

import { IPreciseLocation, ISiteLocation } from '~/client/graph'
import { preciseLocationZoom } from '~/client/src/shared/map/mapZoomRule'
import type InitialState from '~/client/src/shared/stores/InitialState'
import { hasPreciseCoordinates } from '~/client/src/shared/utils/locationSnapshot'

interface IGlobeDefaults {
  id?: string
  bearing?: number
  pitch?: number
  zoom?: number
}

/**
 * Resolves the default globe view id for the Precise Location picker.
 *
 * Priority:
 *   1. The globe a handed-off drawing was made on (drafts are globe-scoped).
 *   2. The map view the pin was dropped or saved on — the one its Location
 *      snapped against.
 *   3. Location-based tag-rule match (from `findBestGlobeForLocation`).
 *   4. First offered globe as a fallback.
 *
 * Only a globe in `globeIds` (the picker's list) can win, so a deleted or unoffered id falls
 * through instead of leaving the picker with nothing selected.
 */
export function resolveDefaultGlobeId(params: {
  adoptedMarkupGlobeId?: string | null
  savedGlobeIdFromPreciseLocation: string | null | undefined
  locationBasedGlobeId: string | null | undefined
  globeIds: string[]
}): string | undefined {
  const {
    adoptedMarkupGlobeId,
    savedGlobeIdFromPreciseLocation,
    locationBasedGlobeId,
    globeIds,
  } = params
  return [
    adoptedMarkupGlobeId,
    savedGlobeIdFromPreciseLocation,
    locationBasedGlobeId,
    globeIds[0],
  ].find(id => id && globeIds.includes(id))
}

/**
 * The globes the Precise Location picker offers: those published to Workflows, plus the globe an
 * existing workflow's pin was saved on, so that pin stays reachable after its globe is unpublished.
 */
export function resolvePreciseLocationGlobeIds(params: {
  state?: Pick<InitialState, 'workflowsMapGlobeViewIdsList' | 'globeViews'>
  store?: { existingPermit?: unknown } | null
  value?: ISiteLocation | null
}): string[] {
  const { workflowsMapGlobeViewIdsList, globeViews } = params.state
  const savedGlobeId = params.value?.preciseLocation?.globeViewId
  if (
    !params.store?.existingPermit ||
    !globeViews.has(savedGlobeId) ||
    workflowsMapGlobeViewIdsList.includes(savedGlobeId)
  ) {
    return workflowsMapGlobeViewIdsList
  }
  return [...workflowsMapGlobeViewIdsList, savedGlobeId]
}

/**
 * The globe a pin is saved on. A duplicate keeps its source's globe, which the picker may not
 * offer (Home-only, deleted), leaving the pin on no Workflows map; such a pin moves to the globe
 * the picker displays it on. Independent of the snapshot, so a skipped or failed capture keeps it.
 */
export function resolvePinGlobeId(params: {
  savedGlobeId: string | null | undefined
  displayedGlobeId: string | null | undefined
  globeIds: string[]
}): string | null | undefined {
  const { savedGlobeId, displayedGlobeId, globeIds } = params
  return savedGlobeId && displayedGlobeId && !globeIds.includes(savedGlobeId)
    ? displayedGlobeId
    : savedGlobeId
}

/**
 * Decides whether the default globe should be recomputed after a prop
 * update. We recompute when the workflow location id changes and the
 * picker is collapsed — expanded means the user is interacting and we
 * must not clobber any manual map swap.
 */
export function shouldRecomputeDefaultGlobe(params: {
  prevLocationId: string | null | undefined
  nextLocationId: string | null | undefined
  isPickerExpanded: boolean
}): boolean {
  if (params.isPickerExpanded) {
    return false
  }
  return params.prevLocationId !== params.nextLocationId
}

/**
 * Builds the next viewport for the Precise Location map.
 *
 * When the preciseLocation was captured on a different map (e.g., click-
 * from-map created a pin on Map A but tag rules switched to Map B), the
 * stored bearing/pitch/zoom are not meaningful for the current map.
 * Preserve the pin coordinates but adopt the current globe's defaults.
 *
 * Returns `null` when there is no preciseLocation — caller should fall
 * back to its default viewport strategy.
 */
export function resolveViewportForPreciseLocation(params: {
  preciseLocation: IPreciseLocation | null | undefined
  selectedGlobe: IGlobeDefaults | null | undefined
  currentViewport: ViewState
}): ViewState | null {
  const { preciseLocation, selectedGlobe, currentViewport } = params
  if (!hasPreciseCoordinates(preciseLocation)) {
    return null
  }

  const { latitude, longitude, globeViewId } = preciseLocation
  const globeIdMismatch =
    selectedGlobe?.id && globeViewId && globeViewId !== selectedGlobe.id

  if (globeIdMismatch) {
    return {
      ...currentViewport,
      latitude,
      longitude,
      bearing: selectedGlobe.bearing ?? 0,
      pitch: selectedGlobe.pitch ?? 0,
      zoom: preciseLocationZoom(selectedGlobe.zoom ?? currentViewport.zoom),
    }
  }

  // Field by field, never a blanket spread: the record carries imageUrl/globeViewId, and an
  // absent camera would clobber the live one — mapbox reads a null zoom as 0, the whole globe.
  return {
    ...currentViewport,
    latitude: preciseLocation.latitude,
    longitude: preciseLocation.longitude,
    // The globe before the live camera: a record with no stored orientation is shown on two
    // different map instances, and only the globe's is the same number on both.
    bearing:
      preciseLocation.bearing ??
      selectedGlobe?.bearing ??
      currentViewport.bearing,
    pitch:
      preciseLocation.pitch ?? selectedGlobe?.pitch ?? currentViewport.pitch,
    zoom: preciseLocationZoom(preciseLocation.zoom),
  }
}

/**
 * Waits for the map the deferred mount (STX1-1127) creates, so a save in that window still has one
 * to photograph. Whether its frame is complete is captureMapScreenshots' job: waiting for 'idle'
 * here failed outright whenever anything on the map animated.
 */
export async function waitForCaptureReadyMap(
  getMap: () => unknown,
  timeoutMs: number,
): Promise<boolean> {
  if (getMap()) {
    return true
  }
  try {
    await when(() => !!getMap(), { timeout: timeoutMs })
    return true
  } catch {
    return false
  }
}
